import { useState, useEffect, useMemo } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { StatusBadge } from "@/components/controller/StatusBadge";
import { supabase } from "@/integrations/supabase/client";
import { useQueryClient } from "@tanstack/react-query";
import { invalidateFinancials } from "@/lib/invalidateProjectFinancials";
import { toast } from "sonner";
import { Loader2, Trash2, Plus, Ban, AlertTriangle } from "lucide-react";
import type { Bill } from "@/hooks/useBills";
import { useVoidBill, useHardDeleteBill } from "@/hooks/useBills";
import { usePermissions } from "@/hooks/usePermissions";
import { useProjects } from "@/hooks/useProjects";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";

const r2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;
const fmt = (n: number) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format(n);

interface LineState {
  id: string;
  description: string;
  quantity: number;
  unit_price: number;
  line_total: number;
  work_order_line_id: string | null;
  isNew?: boolean;
}

interface Props {
  bill: Bill | null;
  open: boolean;
  onClose: () => void;
}

export function BillDetailModal({ bill, open, onClose }: Props) {
  const qc = useQueryClient();
  const voidBill = useVoidBill();
  const hardDelete = useHardDeleteBill();
  const { isAdmin } = usePermissions();
  const [voidReason, setVoidReason] = useState("");
  const [voidOpen, setVoidOpen] = useState(false);
  const [hardDeleteOpen, setHardDeleteOpen] = useState(false);

  // Header fields
  const [billDate, setBillDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [description, setDescription] = useState("");
  const [retainagePct, setRetainagePct] = useState(0);
  const [notes, setNotes] = useState("");
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(null);

  // Projects for reassignment
  const { data: projects } = useProjects();

  // Line items
  const [lines, setLines] = useState<LineState[]>([]);
  const [deletedLines, setDeletedLines] = useState<LineState[]>([]);
  const [linesLoading, setLinesLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Load bill data when opened
  useEffect(() => {
    if (!open || !bill) return;
    setBillDate(bill.bill_date ?? new Date().toISOString().split("T")[0]);
    setDueDate(bill.due_date ?? "");
    setDescription(bill.description ?? "");
    setRetainagePct(Number(bill.retainage_percent ?? 0));
    setNotes((bill as any).notes ?? "");
    setSelectedProjectId(bill.project_id ?? null);
    setDeletedLines([]);

    // Fetch line items and auto-correct stale totals
    setLinesLoading(true);
    (async () => {
      const { data } = await supabase
        .from("bill_lines")
        .select("id, description, quantity, unit_price, line_total, work_order_line_id")
        .eq("bill_id", bill.id)
        .order("created_at");

      setLines(
        (data ?? []).map((l: any) => ({
          id: l.id,
          description: l.description ?? "",
          quantity: Number(l.quantity),
          unit_price: Number(l.unit_price),
          line_total: Number(l.quantity) * Number(l.unit_price),
          work_order_line_id: l.work_order_line_id ?? null,
        })),
      );
      setLinesLoading(false);
    })();
  }, [open, bill?.id]);

  // Calculations
  const updateLine = (idx: number, field: keyof LineState, value: string | number) => {
    setLines((prev) => {
      const next = [...prev];
      const row = { ...next[idx], [field]: value };
      if (field === "quantity" || field === "unit_price") {
        row.line_total = Number(row.quantity) * Number(row.unit_price);
      }
      next[idx] = row;
      return next;
    });
  };

  const deleteLine = (idx: number) => {
    setLines((prev) => {
      const removed = prev[idx];
      if (!removed.isNew) {
        setDeletedLines((d) => [...d, removed]);
      }
      return prev.filter((_, i) => i !== idx);
    });
  };

  const addLine = () => {
    setLines((prev) => [
      ...prev,
      {
        id: `new-${Date.now()}`,
        description: "",
        quantity: 1,
        unit_price: 0,
        line_total: 0,
        work_order_line_id: null,
        isNew: true,
      },
    ]);
  };

  const subtotal = Math.round((lines.reduce((s, l) => s + l.line_total, 0) + Number.EPSILON) * 100) / 100;
  const retainageAmt = Math.round((subtotal * retainagePct / 100 + Number.EPSILON) * 100) / 100;
  const total = Math.round((subtotal - retainageAmt + Number.EPSILON) * 100) / 100;

  // Save
  const handleSave = async () => {
    if (!bill) return;
    setSaving(true);
    try {
      const { error: billErr } = await supabase.rpc("edit_bill" as any, {
        p_bill_id: bill.id,
        p_header: {
          bill_date: billDate,
          due_date: dueDate || null,
          description: description || null,
          retainage_percent: retainagePct,
          retainage_amount: retainageAmt,
          subtotal,
          total,
          ...(selectedProjectId !== (bill.project_id ?? null) ? { project_id: selectedProjectId } : {}),
        },
        p_lines: lines.filter((l) => !l.isNew).map((l) => ({
          id: l.id,
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          line_total: l.line_total,
        })),
        p_new_lines: lines.filter((l) => l.isNew).map((l) => ({
          description: l.description,
          quantity: l.quantity,
          unit_price: l.unit_price,
          line_total: l.line_total,
        })),
        p_deleted_lines: deletedLines.map((dl) => ({
          id: dl.id,
          work_order_line_id: dl.work_order_line_id ?? null,
        })),
      });
      if (billErr) throw billErr;

      // Get company ID for audit log
      const { data: companyId } = await supabase.rpc("my_company_id");

      // Audit log
      try {
        const userId = (await supabase.auth.getUser()).data.user?.id;
        await supabase.from("ops_audit_log").insert({
          company_id: companyId,
          table_name: "bills",
          record_id: bill.id,
          action: selectedProjectId !== (bill.project_id ?? null) ? "bill_project_reassigned" : "bill_edited",
          changed_by: userId,
          metadata: {
            bill_number: bill.bill_number, subtotal, retainage_amount: retainageAmt, total,
            ...(selectedProjectId !== (bill.project_id ?? null) ? { old_project_id: bill.project_id, new_project_id: selectedProjectId } : {}),
          },
        } as any);
      } catch { /* non-blocking */ }

      toast.success(`${bill.bill_number} updated`);
      invalidateFinancials(qc, { billId: bill.id, projectId: selectedProjectId ?? bill.project_id ?? null });
      onClose();
    } catch (e: any) {
      toast.error(e.message ?? "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  if (!bill) return null;

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent hideClose className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <DialogTitle className="font-mono">{bill.bill_number}</DialogTitle>
            <StatusBadge status={bill.status} />
          </div>
          <DialogDescription>
            {bill.vendors?.name ?? "No vendor"} · {bill.projects?.name ?? "No project"}
          </DialogDescription>
        </DialogHeader>

        {/* Header fields */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 py-2">
          <div className="space-y-1.5">
            <Label>Bill Date</Label>
            <Input type="date" value={billDate} onChange={(e) => setBillDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Due Date</Label>
            <Input type="date" value={dueDate} onChange={(e) => setDueDate(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label>Retainage %</Label>
            <Input type="number" step="0.01" min="0" max="100" value={retainagePct} onChange={(e) => setRetainagePct(Number(e.target.value))} />
          </div>
          <div className="space-y-1.5">
            <Label>Status</Label>
            <div className="h-9 flex items-center">
              <StatusBadge status={bill.status} />
            </div>
          </div>
        </div>

        {/* Project reassignment */}
        {bill.status !== "voided" && (
          <div className="space-y-1.5">
            <Label>Project</Label>
            <Select
              value={selectedProjectId ?? "none"}
              onValueChange={(v) => setSelectedProjectId(v === "none" ? null : v)}
            >
              <SelectTrigger>
                <SelectValue placeholder="Select project" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">No project</SelectItem>
                {(projects ?? []).map((p: any) => (
                  <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            {selectedProjectId !== (bill.project_id ?? null) && (
              <div className="flex items-center gap-1.5 text-xs text-amber-600 dark:text-amber-400">
                <AlertTriangle className="h-3 w-3" />
                Project will be reassigned on save.
                {bill.work_order_id && " Warning: this bill is linked to a Work Order on the old project."}
              </div>
            )}
          </div>
        )}

        <div className="space-y-1.5">
          <Label>Description / Memo</Label>
          <Textarea
            placeholder="Bill description or memo"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            rows={2}
          />
        </div>

        {/* Line items */}
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <Label className="text-sm font-semibold">Line Items</Label>
            <Button variant="outline" size="sm" onClick={addLine}>
              <Plus className="mr-1 h-3 w-3" /> Add Line
            </Button>
          </div>

          {linesLoading ? (
            <div className="flex justify-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
            </div>
          ) : (
            <div className="border rounded-md overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted/50 border-b">
                    <th className="text-left px-3 py-2 font-medium w-[40%]">Description</th>
                    <th className="text-right px-3 py-2 font-medium w-[14%]">Qty</th>
                    <th className="text-right px-3 py-2 font-medium w-[18%]">Unit Price</th>
                    <th className="text-right px-3 py-2 font-medium w-[18%]">Amount</th>
                    <th className="px-3 py-2 text-center font-medium w-[12%]">Delete</th>
                  </tr>
                </thead>
                <tbody>
                  {lines.map((line, idx) => (
                    <tr key={line.id} className="border-b last:border-0">
                      <td className="px-2 py-1.5">
                        <Input
                          className="h-8 text-sm"
                          value={line.description}
                          onChange={(e) => updateLine(idx, "description", e.target.value)}
                          placeholder="Line description"
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          className="h-8 text-sm text-right font-mono"
                          type="number" step="0.01" min="0"
                          value={line.quantity}
                          onChange={(e) => updateLine(idx, "quantity", Number(e.target.value))}
                        />
                      </td>
                      <td className="px-2 py-1.5">
                        <Input
                          className="h-8 text-sm text-right font-mono"
                          type="number" step="0.01" min="0"
                          value={line.unit_price}
                          onChange={(e) => updateLine(idx, "unit_price", Number(e.target.value))}
                        />
                      </td>
                      <td className="px-3 py-1.5 text-right font-mono font-medium">
                        {fmt(line.line_total)}
                      </td>
                      <td className="px-2 py-1.5 text-center">
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-8 gap-1 px-2 text-muted-foreground hover:bg-destructive/10 hover:text-destructive"
                          onClick={() => deleteLine(idx)}
                          disabled={lines.length <= 1 && !line.isNew}
                          title="Delete line"
                          type="button"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                          <span>Delete</span>
                        </Button>
                      </td>
                    </tr>
                  ))}
                  {lines.length === 0 && (
                    <tr>
                      <td colSpan={5} className="text-center py-6 text-muted-foreground">
                        No line items. Click "Add Line" to create one.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Deleted lines notice */}
        {deletedLines.length > 0 && (
          <div className="flex items-center gap-2 p-2 rounded-md bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 text-xs">
            <span className="text-amber-700 dark:text-amber-400">
              {deletedLines.length} line{deletedLines.length > 1 ? "s" : ""} will be removed on save.
              {deletedLines.some((d) => d.work_order_line_id) && " Linked WO lines will be restored to billable."}
            </span>
          </div>
        )}

        {/* Totals */}
        <div className="flex justify-end">
          <div className="w-72 text-sm space-y-1.5">
            <div className="flex justify-between">
              <span className="text-muted-foreground">Subtotal</span>
              <span className="font-semibold font-mono">{fmt(subtotal)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-muted-foreground">Retainage ({retainagePct}%)</span>
              <span className="font-mono text-red-600">({fmt(retainageAmt)})</span>
            </div>
            <div className="flex justify-between border-t pt-1.5 font-bold text-base">
              <span>Total</span>
              <span className="font-mono">{fmt(total)}</span>
            </div>
          </div>
        </div>

        <DialogFooter className="sm:justify-between">
          <div className="flex gap-2">
            {bill.status !== "voided" && bill.status !== "paid" && (
              <AlertDialog open={voidOpen} onOpenChange={(v) => { setVoidOpen(v); if (!v) setVoidReason(""); }}>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm">
                    <Ban className="mr-1 h-3.5 w-3.5" /> Void Bill
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Void {bill.bill_number}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will mark the bill as voided and release any linked work order lines back to billable status. This action cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <div className="space-y-1.5 py-2">
                    <Label>Reason (required)</Label>
                    <Textarea
                      placeholder="Why is this bill being voided?"
                      value={voidReason}
                      onChange={(e) => setVoidReason(e.target.value)}
                      rows={2}
                    />
                  </div>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={!voidReason.trim() || voidBill.isPending}
                      onClick={async (e) => {
                        e.preventDefault();
                        await voidBill.mutateAsync({ billId: bill.id, reason: voidReason.trim() });
                        toast.success(`${bill.bill_number} voided`);
                        invalidateFinancials(qc, { billId: bill.id, projectId: bill.project_id ?? null });
                        setVoidOpen(false);
                        onClose();
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {voidBill.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : null}
                      Confirm Void
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
            {isAdmin && (
              <AlertDialog open={hardDeleteOpen} onOpenChange={setHardDeleteOpen}>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" size="sm" className="border-destructive text-destructive hover:bg-destructive/10">
                    <Trash2 className="mr-1 h-3.5 w-3.5" /> Hard Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Permanently Delete {bill.bill_number}?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will <strong>permanently remove</strong> this bill and all its line items from the database.
                      Any linked work order lines will be restored to billable status.
                      This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction
                      disabled={hardDelete.isPending}
                      onClick={async (e) => {
                        e.preventDefault();
                        await hardDelete.mutateAsync(bill.id);
                        toast.success(`${bill.bill_number} permanently deleted`);
                        setHardDeleteOpen(false);
                        onClose();
                      }}
                      className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                    >
                      {hardDelete.isPending ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Trash2 className="mr-1 h-4 w-4" />}
                      Delete Permanently
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            )}
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={onClose}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving || !billDate || bill.status === "voided"}>
              {saving ? <><Loader2 className="mr-1 h-4 w-4 animate-spin" />Saving…</> : "Save Changes"}
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
